/**
 * Expected items on the virtual account's Cashflow forecast (TMMAPP-0112; TMMAPI-0148, MM-D-122;
 * flagged: undrawn in v2, built from the prototype's own patterns). The virtual account's own plan
 * lines ("Expected" in the list), the writer's row actions — Mark received (settle), Dismiss, Edit,
 * Delete — the "+ Expected item" editor (C11, "On {virtual account}", no envelope) and the match
 * prompt: a card above the list asking "Is this the {date} {item}?" for each suggested part, with
 * Mark received (settle with that part) and "Not this one". A match is never applied without a
 * writer's confirmation (MM-D-45). Every member reads the lines; only a writer reads the matches. Each
 * write refetches the lines, the matches and the projection. "Not this one" is a server dismissal
 * (TMMAPI-0152 ask 5: shared by every member, the match list leaves it out) answered with
 * "Dismissed." and Undo (the `DELETE`); a Viewer never gets the button.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError, likelyDuplicate } from '@/api/errors';
import {
  planLinesApi,
  virtualAccountPlanLinesApi,
  type PlanLine,
  type PlanLineKind,
  type PlanMatchCandidate,
  type PlanMatchItem,
} from '@/api/planLines';
import type { RecurrenceRule } from '@/api/planned';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MoneyText } from '@/components/MoneyText';
import { duplicateSentence } from '@/features/envelope/likelyDuplicate';
import { PlanLineDialog } from '@/features/envelope/PlanLineDialogs';
import { applyBackendFields } from '@/features/shared/useFieldErrors';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

/** The plan-line fields of a forecast row the actions need. */
export interface ExpectedRow {
  planLineId: number;
  /** The envelope the line sits on; `null` for a line on the virtual account. */
  envelopeId: number | null;
  /** The occurrence's own date (an overdue item's original date). */
  originalOn: string;
  name: string;
}

type Body = {
  name?: string;
  kind: PlanLineKind;
  amountMinor: number;
  note?: string;
  occursOn?: string;
  recurrence?: RecurrenceRule;
  confirmDuplicate?: boolean;
};

type Editor = { line?: PlanLine } | null;

interface Options {
  virtualAccount: VirtualAccountDetail;
  canWrite: boolean;
  /** Refetch the projection after a write. */
  onChanged: () => Promise<void>;
  onNotify: (message: string) => void;
}

export interface ExpectedItems {
  /** The virtual account's own lines, by id: a forecast row whose line is here is "Expected". */
  lines: ReadonlyMap<number, PlanLine>;
  openCreate: () => void;
  openEdit: (line: PlanLine) => void;
  askDelete: (line: PlanLine) => void;
  markReceived: (row: ExpectedRow) => Promise<void>;
  dismiss: (row: ExpectedRow) => Promise<void>;
  /** The match prompt's live region (a writer's) with its card when something is suggested; `null` for a reader. */
  prompt: ReactNode;
  /** The editor and the delete confirmation; render once. */
  node: ReactNode;
  /**
   * The virtual account's own lines tmm-api flags `likelyDuplicateOf` (TMMAPP-0173 item 9): one
   * "Possible duplicate" note each, with Edit and Delete for a writer; `null` when none.
   */
  duplicates: ReactNode;
}

const candidateKey = (item: PlanMatchItem, candidate: PlanMatchCandidate) =>
  `${item.planLineId}|${item.occursOn}|${candidate.allocationId}`;

export function useExpectedItems({
  virtualAccount,
  canWrite,
  onChanged,
  onNotify,
}: Options): ExpectedItems {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const vaId = virtualAccount.id;
  const currency = virtualAccount.currency;
  const [lines, setLines] = useState<PlanLine[]>([]);
  const [matches, setMatches] = useState<PlanMatchItem[]>([]);
  // Dismissed this visit: each keeps its "Dismissed." row with Undo; the server holds the dismissal.
  const [dismissed, setDismissed] = useState<
    ReadonlyArray<{ key: string; item: PlanMatchItem; candidate: PlanMatchCandidate }>
  >([]);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorTick, setErrorTick] = useState(0);
  const [nameError, setNameError] = useState<string | null>(null);
  // A `409 PLAN_LINE_LIKELY_DUPLICATE` on the editor's write (TMMAPP-0173 item 8): its warning.
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PlanLine | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [settling, setSettling] = useState<string | null>(null);

  const loadLines = useCallback(async () => {
    try {
      setLines(await virtualAccountPlanLinesApi.list(vaId));
    } catch {
      // The forecast still reads; its rows just aren't marked Expected.
    }
  }, [vaId]);

  const loadMatches = useCallback(async () => {
    if (!canWrite) return;
    try {
      setMatches(await virtualAccountPlanLinesApi.matches(vaId));
    } catch {
      setMatches([]);
    }
  }, [vaId, canWrite]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      virtualAccountPlanLinesApi.list(vaId).catch(() => null),
      canWrite ? virtualAccountPlanLinesApi.matches(vaId).catch(() => null) : null,
    ]).then(([nextLines, nextMatches]) => {
      if (cancelled) return;
      if (nextLines !== null) setLines(nextLines);
      setMatches(nextMatches ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [vaId, canWrite]);

  async function refreshAll() {
    await Promise.all([loadLines(), loadMatches(), onChanged()]);
  }

  async function settle(
    planLineId: number,
    envelopeId: number | null,
    body: {
      occurrenceOn: string;
      allocationId?: number;
    },
  ) {
    if (envelopeId === null) await virtualAccountPlanLinesApi.settle(vaId, planLineId, body);
    else await planLinesApi.settle(envelopeId, planLineId, body);
  }

  async function markReceived(row: ExpectedRow) {
    try {
      await settle(row.planLineId, row.envelopeId, { occurrenceOn: row.originalOn });
      onNotify(t('expected.received', { name: row.name }));
      await refreshAll();
    } catch (err) {
      onNotify(errorCopy(t, err, 'expected.errorGeneric'));
    }
  }

  async function dismiss(row: ExpectedRow) {
    try {
      if (row.envelopeId === null) {
        await virtualAccountPlanLinesApi.dismiss(vaId, row.planLineId, row.originalOn);
      } else {
        await planLinesApi.dismiss(row.envelopeId, row.planLineId, row.originalOn);
      }
      onNotify(t('expected.dismissed', { name: row.name }));
      await refreshAll();
    } catch (err) {
      onNotify(errorCopy(t, err, 'expected.errorGeneric'));
    }
  }

  async function accept(item: PlanMatchItem, candidate: PlanMatchCandidate) {
    const key = candidateKey(item, candidate);
    if (settling !== null) return;
    setSettling(key);
    try {
      await settle(item.planLineId, item.envelopeId, {
        occurrenceOn: item.occursOn,
        allocationId: candidate.allocationId,
      });
      onNotify(t('expected.received', { name: item.name ?? '' }));
      await refreshAll();
    } catch (err) {
      onNotify(errorCopy(t, err, 'expected.errorGeneric'));
    } finally {
      setSettling(null);
    }
  }

  /** "Not this one": the dismissal is the server's, for every member (TMMAPI-0152). */
  async function notThisOne(item: PlanMatchItem, candidate: PlanMatchCandidate) {
    const key = candidateKey(item, candidate);
    if (dismissing !== null) return;
    setDismissing(key);
    try {
      await virtualAccountPlanLinesApi.dismissMatch(vaId, {
        planLineId: item.planLineId,
        occursOn: item.occursOn,
        transactionId: candidate.transactionId,
      });
      setDismissed((prev) => [...prev.filter((row) => row.key !== key), { key, item, candidate }]);
      await loadMatches();
    } catch (err) {
      onNotify(errorCopy(t, err, 'expected.errorGeneric'));
    } finally {
      setDismissing(null);
    }
  }

  async function undoDismiss(row: (typeof dismissed)[number]) {
    if (dismissing !== null) return;
    setDismissing(row.key);
    try {
      await virtualAccountPlanLinesApi.undismissMatch(vaId, {
        planLineId: row.item.planLineId,
        occursOn: row.item.occursOn,
        transactionId: row.candidate.transactionId,
      });
      setDismissed((prev) => prev.filter((entry) => entry.key !== row.key));
      await loadMatches();
    } catch (err) {
      onNotify(errorCopy(t, err, 'expected.errorGeneric'));
    } finally {
      setDismissing(null);
    }
  }

  function openEditor(next: Editor) {
    setError(null);
    setNameError(null);
    setDuplicate(null);
    setEditor(next);
  }

  async function submit(body: Body) {
    setBusy(true);
    setError(null);
    setNameError(null);
    setDuplicate(null);
    try {
      const line = editor?.line;
      if (line === undefined) {
        await virtualAccountPlanLinesApi.create(vaId, { ...body, name: body.name ?? '' });
        onNotify(t('expected.added'));
      } else {
        await virtualAccountPlanLinesApi.update(vaId, line.id, body);
        onNotify(t('expected.saved'));
      }
      setEditor(null);
      await refreshAll();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        const result = applyBackendFields(err, ['name'], (_f, message) => setNameError(message), t);
        const backend = err.details.backend;
        if (typeof backend === 'object' && backend !== null && backend.field === 'name') {
          setNameError(t('planLines.nameRequired'));
        } else if (result.placed === 0) setError(t('expected.errorGeneric'));
      } else if (likelyDuplicate(err) !== null) {
        setDuplicate(duplicateSentence(t, likelyDuplicate(err)!, currency, locale));
      } else {
        setError(errorCopy(t, err, 'expected.errorGeneric'));
      }
      setErrorTick((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (deleting === null || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await virtualAccountPlanLinesApi.remove(vaId, deleting.id);
      onNotify(t('expected.deleted'));
      setDeleting(null);
      await refreshAll();
    } catch (err) {
      setDeleteError(errorCopy(t, err, 'expected.errorGeneric'));
    } finally {
      setDeleteBusy(false);
    }
  }

  // One prompt per item: its first suggestion (the server leaves dismissed ones out; one dismissed
  // this visit stays out while the list refetches, its row showing Undo instead).
  const dismissedKeys = new Set(dismissed.map((row) => row.key));
  const prompts = canWrite
    ? matches.flatMap((item) => {
        const candidate = item.candidates.find((c) => !dismissedKeys.has(candidateKey(item, c)));
        return candidate !== undefined ? [{ item, candidate }] : [];
      })
    : [];
  const undoRows = canWrite ? dismissed : [];

  // The polite live region is mounted for a writer before any suggestion arrives, so the first
  // "Is this the …?" is announced (a region inserted with its content usually isn't).
  const prompt = !canWrite ? null : (
    <Box role="status" aria-live="polite" data-match-live="">
      {(prompts.length > 0 || undoRows.length > 0) && (
        <Paper
          component="section"
          aria-labelledby="expected-match-title"
          data-match-prompt=""
          sx={{ p: '16px 20px', mb: 2.5 }}
        >
          <Typography id="expected-match-title" variant="h3" component="h2">
            {t('expected.matchTitle')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2, mb: 1 }}>
            {t('expected.matchSub')}
          </Typography>
          <Stack sx={{ gap: 0 }}>
            {prompts.map(({ item, candidate }) => {
              const key = candidateKey(item, candidate);
              return (
                <Box
                  key={key}
                  data-match={key}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { md: 'center' },
                    gap: 1.5,
                    py: 1.25,
                    borderTop: `1px solid ${tokens.line}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {t('expected.matchQuestion', {
                        date: formatCivilDate(item.occursOn, locale),
                        item: item.name ?? formatMoneyMinor(item.amountMinor, currency, locale),
                      })}
                    </Typography>
                    <Typography component="div" sx={{ fontSize: 13, color: tokens.text2 }}>
                      {candidate.payee ?? t('expected.matchNoName')}{' '}
                      <MoneyText
                        minor={candidate.amountMinor}
                        currency={currency}
                        variant="body2"
                        signed
                      />{' '}
                      {t('expected.matchOn', { date: formatCivilDate(candidate.postedOn, locale) })}
                    </Typography>
                  </Box>
                  <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={settling !== null}
                      onClick={() => void accept(item, candidate)}
                    >
                      {t('expected.markReceived')}
                    </Button>
                    <Button
                      size="small"
                      disabled={dismissing !== null}
                      onClick={() => void notThisOne(item, candidate)}
                    >
                      {t('expected.notThisOne')}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
            {undoRows.map((row) => (
              <Box
                key={row.key}
                data-match-dismissed={row.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  py: 1.25,
                  borderTop: `1px solid ${tokens.line}`,
                }}
              >
                <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
                  {t('expected.matchDismissed')}
                </Typography>
                <Button
                  size="small"
                  disabled={dismissing !== null}
                  onClick={() => void undoDismiss(row)}
                >
                  {t('expected.undo')}
                </Button>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );

  const node = (
    <>
      {editor !== null && (
        <PlanLineDialog
          title={t(editor.line === undefined ? 'expected.newTitle' : 'expected.editTitle')}
          envelopeName={virtualAccount.name}
          currency={currency}
          busy={busy}
          error={error}
          errorTick={errorTick}
          nameError={nameError}
          subscribe={{ required: false, onCancel: () => {} }}
          duplicate={duplicate}
          onDuplicateCancel={() => setDuplicate(null)}
          {...(editor.line !== undefined ? { line: editor.line } : {})}
          onClose={() => setEditor(null)}
          onSubmit={(body) => void submit(body)}
        />
      )}
      {deleting !== null && (
        <ConfirmDialog
          title={t('expected.deleteTitle', { name: deleting.name ?? '' })}
          consequence={t('expected.deleteConsequence')}
          confirmLabel={t('expected.delete')}
          destructive
          busy={deleteBusy}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            if (!deleteBusy) setDeleting(null);
          }}
        />
      )}
    </>
  );

  const flagged = lines.filter((line) => line.likelyDuplicateOf !== null);
  const duplicates =
    flagged.length === 0 ? null : (
      <Stack data-possible-duplicates="" sx={{ gap: 1, mb: 2.5 }}>
        {flagged.map((line) => (
          <Alert
            key={line.id}
            severity="warning"
            data-possible-duplicate={line.id}
            action={
              canWrite ? (
                <Stack direction="row" sx={{ gap: 0.5 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => openEditor({ line })}
                    aria-label={t('expected.editNamed', { name: line.name ?? '' })}
                  >
                    {t('expected.edit')}
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleting(line);
                    }}
                    aria-label={t('expected.deleteNamed', { name: line.name ?? '' })}
                  >
                    {t('expected.delete')}
                  </Button>
                </Stack>
              ) : undefined
            }
          >
            <AlertTitle>
              {t('planLines.possibleDuplicateNamed', { name: line.name ?? '' })}
            </AlertTitle>
            {line.likelyDuplicateOf !== null &&
              duplicateSentence(t, line.likelyDuplicateOf, currency, locale)}
          </Alert>
        ))}
      </Stack>
    );

  return {
    duplicates,
    lines: new Map(lines.map((line) => [line.id, line])),
    openCreate: () => openEditor({}),
    openEdit: (line) => openEditor({ line }),
    askDelete: (line) => {
      setDeleteError(null);
      setDeleting(line);
    },
    markReceived,
    dismiss,
    prompt,
    node,
  };
}
